/**
 * Duplicate confirm page (HF-109).
 *
 * The campaigns list links here so Duplicate can be opened in a new tab. The
 * copy is made only from the Duplicate button's click handler, never on load,
 * so a link preview, prefetch or reopened tab cannot create a campaign.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type CampaignDetailResponse } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { PageHeader } from '../components/ui/page-header';
import { useToast } from '../components/ui/toast';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; campaign: CampaignDetailResponse }
  | { kind: 'not-found' }
  | { kind: 'failed'; message: string };

export function CampaignDuplicate() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success } = useToast();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setState({ kind: 'not-found' });
      return;
    }
    let cancelled = false;
    api
      .getCampaign(id)
      .then((campaign) => {
        if (!cancelled) setState({ kind: 'loaded', campaign });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: 'not-found' });
        } else {
          setState({
            kind: 'failed',
            message: err instanceof Error ? err.message : 'Failed to load campaign',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleDuplicate = async () => {
    if (state.kind !== 'loaded') return;
    setDuplicating(true);
    setError(null);
    try {
      const newCampaign = await api.cloneCampaign(state.campaign.id);
      success(`Duplicated to "${newCampaign.name}"`);
      // Task 023-08: mark the edit screen as a fresh clone so its loaded
      // "<old-slug>-copy" slug is not treated as hand-edited and the first
      // page-title edit regenerates the slug for the new month.
      navigate(`/campaigns/${newCampaign.id}`, { state: { freshClone: true } });
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Clone failed');
      setDuplicating(false);
    }
  };

  if (state.kind === 'loading') {
    return <div className="py-12 text-center text-muted">Loading...</div>;
  }

  if (state.kind === 'not-found') {
    return (
      <div>
        <PageHeader title="Campaign not found" />
        <Button variant="outline" onClick={() => navigate('/campaigns')}>
          Back to campaigns
        </Button>
      </div>
    );
  }

  if (state.kind === 'failed') {
    return (
      <div>
        <PageHeader title="Duplicate campaign" />
        <p role="alert" className="mb-4 text-sm text-rose">
          {state.message}
        </p>
        <Button variant="outline" onClick={() => navigate('/campaigns')}>
          Back to campaigns
        </Button>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={`Duplicate "${state.campaign.name}"?`} />
      <Card className="max-w-xl p-6">
        <p className="mb-4 text-sm text-muted">
          This creates a copy of the campaign and opens it for editing.
        </p>
        {error && (
          <p role="alert" className="mb-4 text-sm text-rose">
            {error}
          </p>
        )}
        <div className="flex items-center gap-2">
          <Button onClick={handleDuplicate} disabled={duplicating}>
            Duplicate
          </Button>
          <Button variant="outline" onClick={() => navigate('/campaigns')}>
            Cancel
          </Button>
        </div>
      </Card>
    </div>
  );
}
